import { FOLDER_SEPARATOR, MAX_FOLDER_DEPTH } from "@dto/preferences";
import { View } from "@dto/views";

/**
 * Pure transforms behind the personal view folders of the sidebar.
 * A folder is identified by its path ("Commercial/Rapports/2025"); the array
 * order of the folder list defines the display order between siblings, and
 * the ordered view-key list of each folder (viewsByFolder) defines the
 * personal display order of the views it holds — the shared View.order only
 * rules the root. Every function returns a new folder data object (never
 * mutates its input); an invalid operation returns null.
 */

export interface FolderData {
    viewFolders?: string[] | null;
    // folder path -> ordered view keys (a key lives in at most one list)
    viewsByFolder?: Record<string, string[]> | null;
}

export interface FolderNode {
    path: string;
    name: string;
    children: FolderNode[];
    views: View[];
}

export interface FolderTree {
    rootFolders: FolderNode[];
    rootViews: View[];
}

export interface FolderDeletionSummary {
    subFolderCount: number;
    views: View[];
    deletableViews: View[];
    protectedViews: View[];
}

export function folderName(path: string): string {
    const idx = path.lastIndexOf(FOLDER_SEPARATOR);
    return idx === -1 ? path : path.slice(idx + 1);
}

export function parentOf(path: string): string | null {
    const idx = path.lastIndexOf(FOLDER_SEPARATOR);
    return idx === -1 ? null : path.slice(0, idx);
}

export function depthOf(path: string): number {
    return path.split(FOLDER_SEPARATOR).length;
}

// Strict ancestors of a path, closest to the root first
export function ancestorsOf(path: string): string[] {
    const segments = path.split(FOLDER_SEPARATOR);
    const ancestors: string[] = [];
    for (let i = 1; i < segments.length; i++) {
        ancestors.push(segments.slice(0, i).join(FOLDER_SEPARATOR));
    }
    return ancestors;
}

// True when path is ancestorPath itself or lives inside its subtree
export function isInSubtree(path: string, ancestorPath: string): boolean {
    return path === ancestorPath || path.startsWith(ancestorPath + FOLDER_SEPARATOR);
}

function folders(data: FolderData | null | undefined): string[] {
    return data?.viewFolders ?? [];
}

function lists(data: FolderData | null | undefined): Record<string, string[]> {
    return data?.viewsByFolder ?? {};
}

function toData(viewFolders: string[], viewsByFolder: Record<string, string[]>): FolderData {
    const cleaned: Record<string, string[]> = {};
    for (const [path, keys] of Object.entries(viewsByFolder)) {
        if (keys.length > 0) {
            cleaned[path] = keys;
        }
    }
    return {
        viewFolders: viewFolders.length > 0 ? viewFolders : null,
        viewsByFolder: Object.keys(cleaned).length > 0 ? cleaned : null,
    };
}

function sortViewsByOrder(views: View[]): View[] {
    return [...views].sort((a, b) => (a.order || 0) - (b.order || 0));
}

// Folder path of a view, or null when the view sits at the root (held by no
// list, or by a list whose folder no longer exists)
export function folderOfView(data: FolderData | null | undefined, viewKey: string): string | null {
    const allLists = lists(data);
    for (const path of folders(data)) {
        if (allLists[path]?.includes(viewKey)) {
            return path;
        }
    }
    return null;
}

/**
 * Builds the displayed tree: folders in array order, each holding its views
 * in its own list order (personal), then the root views sorted by the shared
 * view order. Keys of unknown views are ignored; views held by no valid list
 * show at the root; a key claimed by several lists (defensive) stays in the
 * first folder in display order, like the backend normalization.
 *
 * hiddenViewKeys are the views that exist but are filtered out of the display
 * (the admin "eye" hiding other users' views): a folder whose subtree holds
 * at least one hidden view and no visible one is pruned from the tree — it
 * only "looks" empty and comes back with the eye. A genuinely empty folder
 * (no key, or stale keys only) stays displayed.
 */
export function buildFolderTree(
    data: FolderData | null | undefined,
    visibleViews: View[],
    hiddenViewKeys?: Set<string>
): FolderTree {
    const viewByKey = new Map<string, View>();
    visibleViews.forEach(view => viewByKey.set(view._key, view));

    const nodeByPath = new Map<string, FolderNode>();
    const rootFolders: FolderNode[] = [];
    const claimed = new Set<string>();
    const allLists = lists(data);

    for (const path of folders(data)) {
        if (nodeByPath.has(path)) {
            continue;
        }
        const node: FolderNode = { path, name: folderName(path), children: [], views: [] };
        for (const viewKey of allLists[path] ?? []) {
            if (claimed.has(viewKey)) {
                continue;
            }
            claimed.add(viewKey);
            const view = viewByKey.get(viewKey);
            if (view) {
                node.views.push(view);
            }
        }
        nodeByPath.set(path, node);
        const parent = parentOf(path);
        const parentNode = parent ? nodeByPath.get(parent) : undefined;
        if (parentNode) {
            parentNode.children.push(node);
        } else {
            rootFolders.push(node);
        }
    }

    const rootViews = sortViewsByOrder(visibleViews.filter(view => !claimed.has(view._key)));

    if (hiddenViewKeys && hiddenViewKeys.size > 0) {
        // Any list key of the subtree pointing to a hidden view?
        const subtreeHasHidden = (path: string): boolean =>
            Object.entries(allLists).some(([p, keys]) =>
                isInSubtree(p, path) && keys.some(key => hiddenViewKeys.has(key))
            );

        // Prunes the nodes that only look empty because of the hidden views;
        // returns whether the kept subtree holds at least one visible view
        const pruneNodes = (nodes: FolderNode[]): { kept: FolderNode[]; hasVisible: boolean } => {
            const kept: FolderNode[] = [];
            let hasVisible = false;
            for (const node of nodes) {
                const children = pruneNodes(node.children);
                node.children = children.kept;
                const nodeHasVisible = node.views.length > 0 || children.hasVisible;
                if (nodeHasVisible || !subtreeHasHidden(node.path)) {
                    kept.push(node);
                    hasVisible = hasVisible || nodeHasVisible;
                }
            }
            return { kept, hasVisible };
        };
        return { rootFolders: pruneNodes(rootFolders).kept, rootViews };
    }

    return { rootFolders, rootViews };
}

// Splits a submitted name into trimmed segments ("A/B" creates levels).
// Returns null when a segment is empty.
function nameToSegments(name: string): string[] | null {
    const segments = name.split(FOLDER_SEPARATOR).map(s => s.trim());
    if (segments.some(s => s === "")) {
        return null;
    }
    return segments;
}

/**
 * True when the folder the submitted name resolves to (under parentPath,
 * null = root) already exists. Only the deepest level counts: creating "A/B"
 * while "A" exists is a normal creation.
 */
export function folderExists(data: FolderData | null | undefined, parentPath: string | null, name: string): boolean {
    const segments = nameToSegments(name);
    if (!segments) {
        return false;
    }
    const path = parentPath
        ? parentPath + FOLDER_SEPARATOR + segments.join(FOLDER_SEPARATOR)
        : segments.join(FOLDER_SEPARATOR);
    return folders(data).includes(path);
}

/**
 * True when the folder the submitted name resolves to (under parentPath,
 * null = root) would exceed the depth cap. False on an invalid name (the
 * invalid-name diagnosis takes precedence).
 */
export function exceedsFolderDepth(parentPath: string | null, name: string): boolean {
    const segments = nameToSegments(name);
    if (!segments) {
        return false;
    }
    const parentDepth = parentPath ? depthOf(parentPath) : 0;
    return parentDepth + segments.length > MAX_FOLDER_DEPTH;
}

/**
 * Creates a folder under parentPath (null = root). The name may contain the
 * separator to create several levels at once; missing ancestors are added.
 * Returns the new data and the path of the deepest created folder, or null
 * when the name is invalid or the depth cap is exceeded.
 */
export function createFolder(
    data: FolderData | null | undefined,
    parentPath: string | null,
    name: string
): { data: FolderData; path: string } | null {
    const segments = nameToSegments(name);
    if (!segments || (parentPath !== null && !folders(data).includes(parentPath))) {
        return null;
    }
    const baseSegments = parentPath ? parentPath.split(FOLDER_SEPARATOR) : [];
    if (baseSegments.length + segments.length > MAX_FOLDER_DEPTH) {
        return null;
    }

    const newFolders = [...folders(data)];
    let path = parentPath ?? "";
    for (const segment of segments) {
        path = path === "" ? segment : path + FOLDER_SEPARATOR + segment;
        if (!newFolders.includes(path)) {
            newFolders.push(path);
        }
    }
    return { data: toData(newFolders, { ...lists(data) }), path };
}

// Rewrites the subtree of oldPath under newPath: folder paths and list keys
function rewritePrefix(data: FolderData | null | undefined, oldPath: string, newPath: string): FolderData {
    const newFolders = folders(data).map(p =>
        isInSubtree(p, oldPath) ? newPath + p.slice(oldPath.length) : p
    );
    const newLists: Record<string, string[]> = {};
    for (const [p, keys] of Object.entries(lists(data))) {
        const rewritten = isInSubtree(p, oldPath) ? newPath + p.slice(oldPath.length) : p;
        newLists[rewritten] = [...keys];
    }
    return toData(newFolders, newLists);
}

/**
 * Renames a folder (single segment, separator forbidden). Keeps its position,
 * its subtree and the view lists. Returns null on invalid or already used
 * name.
 */
export function renameFolder(data: FolderData | null | undefined, path: string, newName: string): FolderData | null {
    const name = newName.trim();
    if (name === "" || name.includes(FOLDER_SEPARATOR) || !folders(data).includes(path)) {
        return null;
    }
    const parent = parentOf(path);
    const newPath = parent ? parent + FOLDER_SEPARATOR + name : name;
    if (newPath === path) {
        return toData([...folders(data)], { ...lists(data) });
    }
    if (folders(data).includes(newPath)) {
        return null;
    }
    return rewritePrefix(data, path, newPath);
}

/**
 * Moves the folder srcPath (with its whole subtree) under destParentPath
 * (null = root), right before the sibling beforePath when given (its position
 * otherwise: end of the list). Returns null when the destination is inside the
 * moved subtree, already holds a folder with the same name, or the depth cap
 * would be exceeded.
 */
export function moveFolder(
    data: FolderData | null | undefined,
    srcPath: string,
    destParentPath: string | null,
    beforePath?: string | null
): FolderData | null {
    const allFolders = folders(data);
    if (!allFolders.includes(srcPath)) {
        return null;
    }
    if (destParentPath !== null && (isInSubtree(destParentPath, srcPath) || !allFolders.includes(destParentPath))) {
        return null;
    }

    const newPath = destParentPath ? destParentPath + FOLDER_SEPARATOR + folderName(srcPath) : folderName(srcPath);
    if (newPath !== srcPath && allFolders.some(p => isInSubtree(p, newPath) && !isInSubtree(p, srcPath))) {
        return null;
    }

    // Depth of the deepest moved descendant once re-rooted
    const subtreeDepth = Math.max(...allFolders.filter(p => isInSubtree(p, srcPath)).map(depthOf));
    if (subtreeDepth - depthOf(srcPath) + depthOf(newPath) > MAX_FOLDER_DEPTH) {
        return null;
    }

    // Extract the subtree (kept in relative order), rewrite it, then reinsert
    const movedBlock = allFolders
        .filter(p => isInSubtree(p, srcPath))
        .map(p => newPath + p.slice(srcPath.length));
    const remaining = allFolders.filter(p => !isInSubtree(p, srcPath));

    let insertionIndex = remaining.length;
    if (beforePath && !isInSubtree(beforePath, srcPath)) {
        const idx = remaining.indexOf(beforePath);
        if (idx !== -1) {
            insertionIndex = idx;
        }
    }
    const newFolders = [...remaining.slice(0, insertionIndex), ...movedBlock, ...remaining.slice(insertionIndex)];

    const newLists: Record<string, string[]> = {};
    for (const [p, keys] of Object.entries(lists(data))) {
        const rewritten = isInSubtree(p, srcPath) ? newPath + p.slice(srcPath.length) : p;
        newLists[rewritten] = [...keys];
    }
    return toData(newFolders, newLists);
}

/**
 * Moves a view into a folder (null = back to the root), inserted right before
 * beforeViewKey when given and present in the target list (appended at the
 * end otherwise). The view leaves any list it was held by.
 */
export function moveViewToFolder(
    data: FolderData | null | undefined,
    viewKey: string,
    path: string | null,
    beforeViewKey?: string | null
): FolderData {
    const newLists: Record<string, string[]> = {};
    for (const [p, keys] of Object.entries(lists(data))) {
        newLists[p] = keys.filter(k => k !== viewKey);
    }
    if (path !== null && folders(data).includes(path)) {
        const keys = newLists[path] ?? [];
        const idx = beforeViewKey ? keys.indexOf(beforeViewKey) : -1;
        if (idx === -1) {
            keys.push(viewKey);
        } else {
            keys.splice(idx, 0, viewKey);
        }
        newLists[path] = keys;
    }
    return toData([...folders(data)], newLists);
}

/**
 * Files a copy of a view (its duplicate) right after the source view, in the
 * folder holding the source. Returns null when the source sits at the root:
 * the copy then stays at the root too, placed by the shared view order.
 */
export function fileViewCopy(
    data: FolderData | null | undefined,
    sourceViewKey: string,
    copyViewKey: string
): FolderData | null {
    const path = folderOfView(data, sourceViewKey);
    if (path === null) {
        return null;
    }
    const keys = lists(data)[path];
    const nextViewKey = keys[keys.indexOf(sourceViewKey) + 1] ?? null;
    return moveViewToFolder(data, copyViewKey, path, nextViewKey);
}

/**
 * Feeds the deletion dialog: subfolder count and the visible views of the
 * subtree (in their personal display order), split between the ones the user
 * can delete and the protected ones.
 */
export function folderDeletionSummary(
    data: FolderData | null | undefined,
    path: string,
    visibleViews: View[],
    canDeleteView: (view: View) => boolean
): FolderDeletionSummary {
    const subFolderCount = folders(data).filter(p => isInSubtree(p, path) && p !== path).length;
    const viewByKey = new Map<string, View>();
    visibleViews.forEach(view => viewByKey.set(view._key, view));
    const allLists = lists(data);
    const views: View[] = [];
    const seen = new Set<string>();
    for (const p of folders(data)) {
        if (!isInSubtree(p, path)) {
            continue;
        }
        for (const viewKey of allLists[p] ?? []) {
            if (seen.has(viewKey)) {
                continue;
            }
            seen.add(viewKey);
            const view = viewByKey.get(viewKey);
            if (view) {
                views.push(view);
            }
        }
    }
    return {
        subFolderCount,
        views,
        deletableViews: views.filter(canDeleteView),
        protectedViews: views.filter(view => !canDeleteView(view)),
    };
}

// Deletes the folder and its subtree; the views it held go back to the root
export function deleteFolderToRoot(data: FolderData | null | undefined, path: string): FolderData {
    const newFolders = folders(data).filter(p => !isInSubtree(p, path));
    const newLists: Record<string, string[]> = {};
    for (const [p, keys] of Object.entries(lists(data))) {
        if (!isInSubtree(p, path)) {
            newLists[p] = [...keys];
        }
    }
    return toData(newFolders, newLists);
}

/**
 * After the destructive branch deleted views, removes from the subtree of
 * path every folder that no longer shelters a surviving view (directly or
 * through a descendant), keeping the chains leading to the survivors. All the
 * subtree list keys except the survivors' are dropped.
 */
export function pruneAfterViewsDeletion(
    data: FolderData | null | undefined,
    path: string,
    survivingViewKeys: string[]
): FolderData {
    const survivors = new Set(survivingViewKeys);
    const survivorPaths: string[] = [];
    const newLists: Record<string, string[]> = {};
    for (const [p, keys] of Object.entries(lists(data))) {
        if (!isInSubtree(p, path)) {
            newLists[p] = [...keys];
            continue;
        }
        const kept = keys.filter(k => survivors.has(k));
        if (kept.length > 0) {
            newLists[p] = kept;
            survivorPaths.push(p);
        }
    }
    const newFolders = folders(data).filter(
        p => !isInSubtree(p, path) || survivorPaths.some(sp => isInSubtree(sp, p))
    );
    return toData(newFolders, newLists);
}
